import {
  ExamCourseCertificateAverageScoreCondition,
  ExamCourseCertificateFailedTemplate,
  ExamCourseCertificatePassedTemplate,
  QuestionnaireTemplateId,
  type UpdateExamCourseSettingsInput,
  type GetExamCourseSettingsByExamCourseCodeQuery,
} from '@exam-center/graphql';
import { type FieldValues } from 'react-hook-form';

import { AVERAGE_SCORE_TEMPLATES } from '../../constants';

type ExamCourseSettings =
  GetExamCourseSettingsByExamCourseCodeQuery['ecGetExamCourseByExamCourseCode']['examCourse']['settings'];

type PassedTemplate =
  | 'average-score-by-part'
  | 'average-score-by-subject'
  | 'no-score'
  | 'section-score'
  | '';

type FailTemplate =
  | 'average-score-by-part'
  | 'average-score-by-subject'
  | 'no-score'
  | 'section-score'
  | '';

const failedTemplateMap = {
  'average-score-by-part': ExamCourseCertificateFailedTemplate.AverageScoreByPart,
  'average-score-by-subject': ExamCourseCertificateFailedTemplate.AverageScoreBySubject,
  'no-score': ExamCourseCertificateFailedTemplate.NoScore,
  'section-score': ExamCourseCertificateFailedTemplate.SectionScore,
} as const;

const passedTemplateMap = {
  'average-score-by-part': ExamCourseCertificatePassedTemplate.AverageScoreByPart,
  'average-score-by-subject': ExamCourseCertificatePassedTemplate.AverageScoreBySubject,
  'no-score': ExamCourseCertificatePassedTemplate.NoScore,
  'section-score': ExamCourseCertificatePassedTemplate.SectionScore,
} as const;

const averageScoreConditionMap = {
  'all-centers': ExamCourseCertificateAverageScoreCondition.AllCenters,
  'each-center': ExamCourseCertificateAverageScoreCondition.EachCenter,
} as const;

const QUESTIONNAIRE_TEMPLATE_MAP = {
  'template-a': QuestionnaireTemplateId.TemplateA,
  'template-b': QuestionnaireTemplateId.TemplateB,
  'template-c': QuestionnaireTemplateId.TemplateC,
} as const;

function mapAverageScoreCondition(
  passTemplate: PassedTemplate | '',
  failTemplate: FailTemplate | '',
  averageScoreCondition?: 'all-centers' | 'each-center',
): ExamCourseCertificateAverageScoreCondition | null {
  if (!averageScoreCondition) return null;

  if (
    AVERAGE_SCORE_TEMPLATES.includes(passTemplate) ||
    AVERAGE_SCORE_TEMPLATES.includes(failTemplate)
  ) {
    return averageScoreConditionMap[averageScoreCondition];
  }

  return null;
}

function mapExistingExamDurationToInput(
  examDuration: ExamCourseSettings['examDuration'] | null | undefined,
): UpdateExamCourseSettingsInput['settings']['examDuration'] {
  if (!examDuration) {
    return null;
  }

  return {
    type: examDuration.type,
    sessions: examDuration.sessions.map((session) => ({
      order: session.order,
      value: session.value,
    })),
  };
}

export async function getAttachDocumentFormData(
  attachDocument: {
    enabled: 'true' | 'false';
    file: File | { name: string; uploadFileName: string } | null;
  },
  handleUpload: (
    file: File,
  ) => Promise<{ uploadFileName: string; originalFileName: string } | null>,
) {
  const isAttachDocumentEnabled = attachDocument.enabled === 'true';
  const attachDocumentFile = attachDocument.file;

  if (isAttachDocumentEnabled && attachDocumentFile instanceof File) {
    const uploadResult = await handleUpload(attachDocumentFile);
    if (!uploadResult) {
      throw new Error('Failed to upload attach document file');
    }
    return uploadResult;
  }

  if (
    isAttachDocumentEnabled &&
    attachDocumentFile !== null &&
    !(attachDocumentFile instanceof File)
  ) {
    return {
      uploadFileName: attachDocumentFile.uploadFileName,
      originalFileName: attachDocumentFile.name,
    };
  }

  return null;
}

export function mapExamCourseSettingsFormInputsToExamCourseSettingsInput(
  data: FieldValues,
  uploadResult: { originalFileName: string; uploadFileName: string } | null,
) {
  return {
    resultDisplay: {
      examResult: data.resultDisplay.shouldDisplay === 'true',
    },
    resultSend: {
      portal: data.resultSend.portal === 'true',
    },
    certificate:
      data.certificate.shouldIssue === 'true'
        ? {
            averageScoreCondition: data.certificate.averageScoreCondition
              ? mapAverageScoreCondition(
                  data.certificate.template.passTemplate,
                  data.certificate.template.failTemplate,
                  data.certificate.averageScoreCondition,
                )
              : null,
            failedTemplate:
              failedTemplateMap[
                data.certificate.template.failTemplate as keyof typeof failedTemplateMap
              ],
            passedTemplate:
              passedTemplateMap[
                data.certificate.template.passTemplate as keyof typeof passedTemplateMap
              ],
            delayedDays:
              data.certificate.deliveryTime.type === 'delay' && data.certificate.deliveryTime.delay
                ? data.certificate.deliveryTime.delay
                : 0,
            validityDurationYears:
              data.certificate.expiry.type === 'delay' && data.certificate.expiry.delay
                ? data.certificate.expiry.delay
                : null,
          }
        : null,
    questionnaire: {
      templateId:
        data.questionnaire.enabled === 'true'
          ? QUESTIONNAIRE_TEMPLATE_MAP[
              data.questionnaire.templateId as keyof typeof QUESTIONNAIRE_TEMPLATE_MAP
            ]
          : null,
    },
    attachDocument:
      data.attachDocument.enabled === 'true' && uploadResult
        ? {
            originalFileName: uploadResult.originalFileName,
            uploadFileName: uploadResult.uploadFileName,
          }
        : null,
  };
}

export function mergeDuringAndPostExamSettingsInput(
  existingSettings: ExamCourseSettings | null | undefined,
  newSettings: UpdateExamCourseSettingsInput['settings'],
) {
  if (!existingSettings) {
    throw new Error('Existing settings must be provided for merging');
  }

  return {
    resultDisplay: {
      examResult: newSettings.resultDisplay?.examResult,
    },
    resultSend: {
      portal: newSettings.resultSend?.portal,
    },
    certificate: newSettings.certificate,
    examDuration: mapExistingExamDurationToInput(existingSettings.examDuration),
    questionnaire: newSettings.questionnaire,
    attachDocument: newSettings.attachDocument,
  };
}
